import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface TournamentInfo {
  id: string;
  name: string;
  date: string;
}

interface LeaderboardEntry {
  position: number;
  name: string;
  points: number;
  isCurrentUser: boolean;
  pointsPerTournament: Record<string, number>;
}

export const useCircuitLeaderboard = (
  circuitId: string | null,
  category: string,
  subcategory: string
) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [tournaments, setTournaments] = useState<TournamentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!circuitId) {
      setLeaderboard([]);
      setTournaments([]);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      try {
        // Fetch leaderboard entries with points_per_tournament
        const { data: entries } = await supabase
          .from('ranking_circuit_leaderboard')
          .select('user_id, position, total_points, is_external, external_name, points_per_tournament')
          .eq('circuit_id', circuitId)
          .eq('category', category)
          .eq('subcategory', subcategory)
          .order('position', { ascending: true });

        // Fetch tournament IDs for this circuit
        const { data: circuitTournaments } = await supabase
          .from('ranking_circuit_tournaments')
          .select('tournament_id')
          .eq('circuit_id', circuitId);

        if (!entries?.length) {
          setLeaderboard([]);
          setTournaments([]);
          setLoading(false);
          return;
        }

        const tournamentIds = (circuitTournaments || []).map(t => t.tournament_id);

        // Resolve tournament names from events and external_tournaments in parallel
        const [eventsRes, extRes] = await Promise.all([
          tournamentIds.length
            ? supabase.from('events').select('id, title, start_date').in('id', tournamentIds)
            : { data: [] },
          tournamentIds.length
            ? supabase.from('external_tournaments').select('id, name, tournament_date').in('id', tournamentIds)
            : { data: [] },
        ]);

        const tournamentMap = new Map<string, TournamentInfo>();
        (eventsRes.data || []).forEach(e => {
          tournamentMap.set(e.id, { id: e.id, name: e.title, date: e.start_date });
        });
        (extRes.data || []).forEach(e => {
          tournamentMap.set(e.id, { id: e.id, name: e.name, date: e.tournament_date });
        });

        // Sort tournaments by date descending
        const sortedTournaments = Array.from(tournamentMap.values())
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setTournaments(sortedTournaments);

        // Resolve user names
        const userIds = entries
          .filter(e => !e.is_external && e.user_id && !e.user_id.startsWith('ext-'))
          .map(e => e.user_id);

        let profileMap = new Map<string, string>();
        if (userIds.length) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('user_id, full_name')
            .in('user_id', userIds);
          profileMap = new Map(
            (profiles || []).map(p => [p.user_id, p.full_name || 'Jugador'])
          );
        }

        const results: LeaderboardEntry[] = entries.map(e => ({
          position: e.position,
          name: e.is_external
            ? (e.external_name || 'Jugador externo')
            : (profileMap.get(e.user_id) || 'Jugador'),
          points: e.total_points,
          isCurrentUser: user?.id === e.user_id,
          pointsPerTournament: (e.points_per_tournament as Record<string, number>) || {},
        }));

        setLeaderboard(results);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        setLeaderboard([]);
        setTournaments([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [circuitId, category, subcategory, user?.id]);

  return { leaderboard, tournaments, loading };
};
